import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Breadcrumb from '../../components/sections/Breadcrumb';

const NominationGuide: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <Breadcrumb 
        title="How to Nominate" 
        subtitle="Nomination Guide" 
      />

      {/* Hero Section */}
      <section 
        className="py-12 md:py-20 bg-white"
        style={{
          padding: '3rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-5xl mx-auto text-center"
            style={{
              maxWidth: '64rem',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900"
              style={{
                fontSize: 'clamp(1.875rem, 4vw, 3rem)',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1.5rem'
              }}
            >
              How to Nominate for Digital Awards
            </h1>
            <p 
              className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed"
              style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                color: '#4B5563',
                lineHeight: '1.75',
                marginBottom: '2rem'
              }}
            >
              Simple steps to recognize excellence in Tanzania's digital landscape
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              {isAuthenticated ? (
                <Link 
                  to="/nominate-form"
                  className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-orange-500 text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-orange-600 transition-colors duration-300 shadow-lg"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#ffba17',
                    color: 'white',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '1rem',
                    textDecoration: 'none',
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.2)'
                  }}
                >
                  <i className="fa-solid fa-trophy mr-2 sm:mr-3"></i>
                  Start Nominating Now
                </Link>
              ) : (
                <>
                  <a 
                    href="https://vote.digitalawards.co.tz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-orange-500 text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-orange-600 transition-colors duration-300 shadow-lg"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#ffba17',
                      color: 'white',
                      borderRadius: '3rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      textDecoration: 'none',
                      transition: 'background-color 0.3s ease',
                      boxShadow: '0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.2)'
                    }}
                  >
                    <i className="fa-solid fa-sign-in-alt mr-2 sm:mr-3"></i>
                    Login to Nominate
                  </a>
                  <a 
                    href="https://vote.digitalawards.co.tz/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-white text-blue-900 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors duration-300 shadow-lg border border-gray-200"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'white',
                      color: '#0f1b3c',
                      borderRadius: '3rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      textDecoration: 'none',
                      transition: 'background-color 0.3s ease',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    <i className="fa-solid fa-user-plus mr-2 sm:mr-3"></i>
                    Create Account
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What is Nomination Section */}
      <section 
        className="py-20 bg-gray-50"
        style={{
          padding: '5rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.5rem'
              }}
            >
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden md:hover:shadow-2xl md:transition-all md:duration-300"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="p-6 md:p-8 bg-gradient-to-br from-blue-900 to-blue-800 md:rounded-xl"
                  style={{
                    padding: '2rem',
                    background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)',
                    borderRadius: '0.75rem'
                  }}
                >
                  <div 
                    className="w-16 h-16 bg-white bg-opacity-20 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4"
                    style={{
                      width: '4rem',
                      height: '4rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginBottom: '1rem'
                    }}
                  >
                    <i className="fa-solid fa-user-plus"></i>
                  </div>
                  <h3 
                    className="text-2xl font-bold mb-4 text-white"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginBottom: '1rem',
                      color: 'white'
                    }}
                  >
                    What is Nomination?
                  </h3>
                  <p 
                    className="text-blue-100 leading-relaxed"
                    style={{
                      color: '#DBEAFE',
                      lineHeight: '1.75'
                    }}
                  >
                    The process where you submit someone (or yourself) for consideration in specific award categories. 
                    This is the first step where we gather information about potential award recipients.
                  </p>
                </div>
              </div>
              
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden md:hover:shadow-2xl md:transition-all md:duration-300"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="p-6 md:p-8 bg-gradient-to-br from-orange-500 to-orange-600 md:rounded-xl"
                  style={{
                    padding: '2rem',
                    background: 'linear-gradient(135deg, #ffba17 0%, #c2410c 100%)',
                    borderRadius: '0.75rem'
                  }}
                >
                  <div 
                    className="w-16 h-16 bg-white bg-opacity-20 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4"
                    style={{
                      width: '4rem',
                      height: '4rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginBottom: '1rem'
                    }}
                  >
                    <i className="fa-solid fa-vote-yea"></i>
                  </div>
                  <h3 
                    className="text-2xl font-bold mb-4 text-white"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginBottom: '1rem',
                      color: 'white'
                    }}
                  >
                    What is Voting?
                  </h3>
                  <p 
                    className="text-orange-100 leading-relaxed"
                    style={{
                      color: '#FED7AA',
                      lineHeight: '1.75'
                    }}
                  >
                    The stage where people choose amongst the nominees to select the final winners. 
                    This happens after the nomination period closes and finalists are announced.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Nominate Section */}
      <section 
        className="py-20 bg-white"
        style={{
          padding: '5rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="text-center mb-12"
              style={{
                textAlign: 'center',
                marginBottom: '3rem'
              }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6 text-gray-900"
                style={{
                  fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1.5rem'
                }}
              >
                Who Can Nominate?
              </h2>
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible'
                }}
              >
                <div 
                  className="p-4 md:p-10"
                  style={{
                    padding: '1rem 2.5rem'
                  }}
                >
                  <div 
                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{
                      width: '5rem',
                      height: '5rem',
                      backgroundColor: '#DCFCE7',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem'
                    }}
                  >
                    <i 
                      className="fa-solid fa-check text-3xl text-green-600"
                      style={{
                        fontSize: '1.875rem',
                        color: '#059669'
                      }}
                    ></i>
                  </div>
                  <p 
                    className="text-xl text-gray-700 leading-relaxed mb-8 text-left md:text-center"
                    style={{
                      fontSize: '1.25rem',
                      color: '#374151',
                      lineHeight: '1.75',
                      marginBottom: '2rem',
                      textAlign: 'left'
                    }}
                  >
                    <strong 
                      className="text-blue-900"
                      style={{ color: '#0f1b3c', fontWeight: '700' }}
                    >
                      Everyone can nominate!
                    </strong> Whether you're nominating yourself, 
                    your organization, or someone else's work that you admire, our platform is open to all.
                  </p>
                  <div 
                    className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '1.5rem'
                    }}
                  >
                    <div 
                      className="bg-blue-50 p-6 rounded-lg hover:shadow-md transition-shadow duration-300"
                      style={{
                        backgroundColor: '#EFF6FF',
                        padding: '1.5rem',
                        borderRadius: '0.5rem',
                        transition: 'box-shadow 0.3s ease'
                      }}
                    >
                      <i 
                        className="fa-solid fa-user text-2xl text-blue-600 mb-3"
                        style={{
                          fontSize: '1.5rem',
                          color: '#2563EB',
                          marginBottom: '0.75rem'
                        }}
                      ></i>
                      <h4 
                        className="font-semibold mb-2 text-gray-900"
                        style={{
                          fontWeight: '600',
                          marginBottom: '0.5rem',
                          color: '#111827'
                        }}
                      >
                        Self Nomination
                      </h4>
                      <p 
                        className="text-gray-600 text-sm"
                        style={{
                          color: '#4B5563',
                          fontSize: '0.875rem'
                        }}
                      >
                        Nominate yourself or your own work
                      </p>
                    </div>
                    <div 
                      className="bg-orange-50 p-6 rounded-lg hover:shadow-md transition-shadow duration-300"
                      style={{
                        backgroundColor: '#FFF7ED',
                        padding: '1.5rem',
                        borderRadius: '0.5rem',
                        transition: 'box-shadow 0.3s ease'
                      }}
                    >
                      <i 
                        className="fa-solid fa-users text-2xl text-orange-600 mb-3"
                        style={{
                          fontSize: '1.5rem',
                          color: '#c2410c',
                          marginBottom: '0.75rem'
                        }}
                      ></i>
                      <h4 
                        className="font-semibold mb-2 text-gray-900"
                        style={{
                          fontWeight: '600',
                          marginBottom: '0.5rem',
                          color: '#111827'
                        }}
                      >
                        Organization
                      </h4>
                      <p 
                        className="text-gray-600 text-sm"
                        style={{
                          color: '#4B5563',
                          fontSize: '0.875rem'
                        }}
                      >
                        Nominate your company or team
                      </p>
                    </div>
                    <div 
                      className="bg-green-50 p-6 rounded-lg hover:shadow-md transition-shadow duration-300"
                      style={{
                        backgroundColor: '#F0FDF4',
                        padding: '1.5rem',
                        borderRadius: '0.5rem',
                        transition: 'box-shadow 0.3s ease'
                      }}
                    >
                      <i 
                        className="fa-solid fa-handshake text-2xl text-green-600 mb-3"
                        style={{
                          fontSize: '1.5rem',
                          color: '#059669',
                          marginBottom: '0.75rem'
                        }}
                      ></i>
                      <h4 
                        className="font-semibold mb-2 text-gray-900"
                        style={{
                          fontWeight: '600',
                          marginBottom: '0.5rem',
                          color: '#111827'
                        }}
                      >
                        Others
                      </h4>
                      <p 
                        className="text-gray-600 text-sm"
                        style={{
                          color: '#4B5563',
                          fontSize: '0.875rem'
                        }}
                      >
                        Nominate someone else's work
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Nominate Steps */}
      <section 
        className="py-20 bg-gray-50"
        style={{
          padding: '5rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="text-center mb-12"
              style={{
                textAlign: 'center',
                marginBottom: '3rem'
              }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6 text-gray-900"
                style={{
                  fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1.5rem'
                }}
              >
                How to Submit a Nomination
              </h2>
              <p 
                className="text-xl text-gray-600"
                style={{
                  fontSize: '1.25rem',
                  color: '#4B5563'
                }}
              >
                Follow these simple steps to submit your nomination
              </p>
            </div>

            <div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '2rem'
              }}
            >
              {/* Step 1 */}
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden md:hover:shadow-2xl md:transition-all md:duration-300 text-center"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  textAlign: 'center'
                }}
              >
                <div 
                  className="p-4 md:p-8"
                  style={{
                    padding: '1rem 2rem'
                  }}
                >
                  <div 
                    className="w-20 h-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                    style={{
                      width: '5rem',
                      height: '5rem',
                      background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      margin: '0 auto 1.5rem'
                    }}
                  >
                    1
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      color: '#111827'
                    }}
                  >
                    Create Account
                  </h3>
                  <p 
                    className="text-gray-600 leading-relaxed text-left md:text-center"
                    style={{
                      color: '#4B5563',
                      lineHeight: '1.75',
                      textAlign: 'left'
                    }}
                  >
                    Sign up for a free account or login if you already have one. 
                    This ensures we can contact you about your nomination.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden md:hover:shadow-2xl md:transition-all md:duration-300 text-center"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  textAlign: 'center'
                }}
              >
                <div 
                  className="p-4 md:p-8"
                  style={{
                    padding: '1rem 2rem'
                  }}
                >
                  <div 
                    className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                    style={{
                      width: '5rem',
                      height: '5rem',
                      background: 'linear-gradient(135deg, #ffba17 0%, #c2410c 100%)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      margin: '0 auto 1.5rem'
                    }}
                  >
                    2
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      color: '#111827'
                    }}
                  >
                    Choose Category
                  </h3>
                  <p 
                    className="text-gray-600 leading-relaxed text-left md:text-center"
                    style={{
                      color: '#4B5563',
                      lineHeight: '1.75',
                      textAlign: 'left'
                    }}
                  >
                    Select the most appropriate category for your nomination. 
                    We have various categories covering different aspects of digital innovation.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div 
                className="md:bg-white md:rounded-xl md:shadow-lg md:overflow-hidden md:hover:shadow-2xl md:transition-all md:duration-300 text-center"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '0',
                  boxShadow: 'none',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  textAlign: 'center'
                }}
              >
                <div 
                  className="p-4 md:p-8"
                  style={{
                    padding: '1rem 2rem'
                  }}
                >
                  <div 
                    className="w-20 h-20 bg-gradient-to-br from-green-600 to-green-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                    style={{
                      width: '5rem',
                      height: '5rem',
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      margin: '0 auto 1.5rem'
                    }}
                  >
                    3
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      color: '#111827'
                    }}
                  >
                    Submit Details
                  </h3>
                  <p 
                    className="text-gray-600 leading-relaxed text-left md:text-center"
                    style={{
                      color: '#4B5563',
                      lineHeight: '1.75',
                      textAlign: 'left'
                    }}
                  >
                    Fill out the nomination form with nominee information, 
                    achievements, and why they deserve recognition.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Information is Required */}
      <section 
        className="py-20 bg-white"
        style={{
          padding: '5rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="text-center mb-12"
              style={{
                textAlign: 'center',
                marginBottom: '3rem'
              }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6 text-gray-900"
                style={{
                  fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1.5rem'
                }}
              >
                What Information Do We Need?
              </h2>
              <p 
                className="text-xl text-gray-600"
                style={{
                  fontSize: '1.25rem',
                  color: '#4B5563'
                }}
              >
                We need three main types of information for each nomination
              </p>
            </div>

            <div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '2rem'
              }}
            >
              {/* Nominator Info */}
              <div 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="p-8"
                  style={{
                    padding: '2rem'
                  }}
                >
                  <div 
                    className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{
                      width: '4rem',
                      height: '4rem',
                      backgroundColor: '#EFF6FF',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}
                  >
                    <i 
                      className="fa-solid fa-user text-2xl text-blue-600"
                      style={{
                        fontSize: '1.5rem',
                        color: '#2563EB'
                      }}
                    ></i>
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-center text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      textAlign: 'center',
                      color: '#111827'
                    }}
                  >
                    Nominator Details
                  </h3>
                  <ul 
                    className="space-y-2 text-gray-600"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      color: '#4B5563'
                    }}
                  >
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Your full name
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Email address
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Phone number
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Organization (optional)
                    </li>
                  </ul>
                </div>
              </div>

              {/* Nominee Info */}
              <div 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="p-8"
                  style={{
                    padding: '2rem'
                  }}
                >
                  <div 
                    className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{
                      width: '4rem',
                      height: '4rem',
                      backgroundColor: '#FFF7ED',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}
                  >
                    <i 
                      className="fa-solid fa-star text-2xl text-orange-600"
                      style={{
                        fontSize: '1.5rem',
                        color: '#c2410c'
                      }}
                    ></i>
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-center text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      textAlign: 'center',
                      color: '#111827'
                    }}
                  >
                    Nominee Information
                  </h3>
                  <ul 
                    className="space-y-2 text-gray-600"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      color: '#4B5563'
                    }}
                  >
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Full name
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Contact details
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Organization/role
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Key achievements
                    </li>
                  </ul>
                </div>
              </div>

              {/* Category */}
              <div 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="p-8"
                  style={{
                    padding: '2rem'
                  }}
                >
                  <div 
                    className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{
                      width: '4rem',
                      height: '4rem',
                      backgroundColor: '#F0FDF4',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}
                  >
                    <i 
                      className="fa-solid fa-trophy text-2xl text-green-600"
                      style={{
                        fontSize: '1.5rem',
                        color: '#059669'
                      }}
                    ></i>
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-4 text-center text-gray-900"
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      textAlign: 'center',
                      color: '#111827'
                    }}
                  >
                    Category Selection
                  </h3>
                  <ul 
                    className="space-y-2 text-gray-600"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      color: '#4B5563'
                    }}
                  >
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Choose appropriate category
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Explain why they fit
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Describe their impact
                    </li>
                    <li className="flex items-start">
                      <i className="fa-solid fa-check text-green-500 mt-1 mr-2 text-sm" style={{ color: '#10B981', marginTop: '0.25rem', marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
                      Provide supporting details
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section 
        className="py-20 bg-gray-50"
        style={{
          padding: '5rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="text-center mb-12"
              style={{
                textAlign: 'center',
                marginBottom: '3rem'
              }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6 text-gray-900"
                style={{
                  fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1.5rem'
                }}
              >
                Award Categories
              </h2>
              <p 
                className="text-xl text-gray-600"
                style={{
                  fontSize: '1.25rem',
                  color: '#4B5563'
                }}
              >
                Choose from our diverse range of categories
              </p>
            </div>

            <div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem'
              }}
            >
              {[
                'Digital Innovation',
                'Digital Marketing Excellence',
                'E-Commerce Excellence',
                'Digital Content Creator',
                'Digital Transformation',
                'FinTech Innovation',
                'EdTech Innovation',
                'HealthTech Innovation',
                'AgriTech Innovation',
                'Digital Startup of the Year',
                'Digital Leader of the Year',
                'Social Impact Technology'
              ].map((category, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-lg border border-gray-100 hover:shadow-xl hover:border-blue-300 transition-all duration-300"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #F3F4F6',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div 
                    className="p-6"
                    style={{
                      padding: '1.5rem'
                    }}
                  >
                    <div 
                      className="flex items-center"
                      style={{
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3"
                        style={{
                          width: '2rem',
                          height: '2rem',
                          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          marginRight: '0.75rem'
                        }}
                      >
                        {index + 1}
                      </div>
                      <h3 
                        className="font-semibold text-gray-900"
                        style={{
                          fontWeight: '600',
                          color: '#111827'
                        }}
                      >
                        {category}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section 
        className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white"
        style={{
          padding: '5rem 0',
          background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-4xl mx-auto text-center"
            style={{
              maxWidth: '64rem',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{
                fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                fontWeight: '700',
                marginBottom: '1.5rem'
              }}
            >
              Ready to Recognize Excellence?
            </h2>
            <p 
              className="text-xl mb-8 text-blue-100 leading-relaxed text-left md:text-center"
              style={{
                fontSize: '1.25rem',
                marginBottom: '2rem',
                color: '#DBEAFE',
                lineHeight: '1.75'
              }}
            >
              Don't miss this opportunity to highlight the achievements that are shaping Tanzania's digital future. 
              Submit your nomination today!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              {isAuthenticated ? (
                <Link 
                  to="/nominate-form"
                  className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-orange-500 text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-orange-600 transition-colors duration-300 shadow-lg"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#ffba17',
                    color: 'white',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '1rem',
                    textDecoration: 'none',
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.2)'
                  }}
                >
                  <i className="fa-solid fa-paper-plane mr-2 sm:mr-3"></i>
                  Submit Nomination Now
                </Link>
              ) : (
                <>
                  <a 
                    href="https://vote.digitalawards.co.tz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-orange-500 text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-orange-600 transition-colors duration-300 shadow-lg"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#ffba17',
                      color: 'white',
                      borderRadius: '3rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      textDecoration: 'none',
                      transition: 'background-color 0.3s ease',
                      boxShadow: '0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.2)'
                    }}
                  >
                    <i className="fa-solid fa-sign-in-alt mr-2 sm:mr-3"></i>
                    Login to Nominate
                  </a>
                  <a 
                    href="https://vote.digitalawards.co.tz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-white text-blue-900 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors duration-300 shadow-lg"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'white',
                      color: '#0f1b3c',
                      borderRadius: '3rem',
                      fontWeight: '600',
                      fontSize: '1rem',
                      textDecoration: 'none',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <i className="fa-solid fa-user-plus mr-2 sm:mr-3"></i>
                    Create Free Account
                  </a>
                </>
              )}
            </div>
            <p 
              className="text-blue-200 mt-6 text-sm"
              style={{
                color: '#BFDBFE',
                marginTop: '1.5rem',
                fontSize: '0.875rem'
              }}
            >
              <i className="fa-solid fa-shield-alt mr-2"></i>
              Free to nominate • Secure submission • No hidden fees
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NominationGuide;
